import type { DealerGroup, Dealership } from "@lyteflo/types";
import {
  DEALER_CONTEXT_TYPE,
  hasGroupRole,
  hasLytefloRole,
  UNGROUPED_GROUP_ID,
  useDealerContextStore,
  useDealerGroups,
  useProfile,
} from "@lyteflo/utils";

export interface DealershipWithGroup extends Dealership {
  groupName?: string;
}

export const useDealerContext = () => {
  const { profile } = useProfile();
  const { dealerGroups, isLoading } = useDealerGroups();
  const context = useDealerContextStore((s) => s.context);
  const setContext = useDealerContextStore((s) => s.setContext);

  const isGroupRole = profile ? hasGroupRole(profile) : false;
  const isLytefloUser = profile ? hasLytefloRole(profile) : false;

  if (!profile || !dealerGroups) {
    return {
      availableDealerships: [] as DealershipWithGroup[],
      dealerGroups: [] as DealerGroup[],
      context,
      setContext,
      isLoading,
      isGroupRole,
      isLytefloUser,
    };
  }

  const filteredGroups = isLytefloUser
    ? dealerGroups
    : isGroupRole
      ? dealerGroups.filter(
          (group) =>
            profile.groups?.some((g) => g.id === group.id) ||
            (group.id === UNGROUPED_GROUP_ID &&
              group.dealers?.some((dealer) =>
                profile.dealers?.some(
                  (userDealer) =>
                    userDealer.configurationId === dealer.configurationId,
                ),
              )),
        )
      : dealerGroups.filter((group) =>
          group.dealers?.some((dealer) =>
            profile.dealers?.some(
              (userDealer) =>
                userDealer.configurationId === dealer.configurationId,
            ),
          ),
        );

  const sortedGroups = filteredGroups.sort((a, b) => {
    if (a.id === UNGROUPED_GROUP_ID) return 1;
    if (b.id === UNGROUPED_GROUP_ID) return -1;
    return a.name.localeCompare(b.name);
  });

  const sortedGroupsAndDealers = sortedGroups.map((group) => ({
    ...group,
    dealers: group.dealers?.sort((a, b) => a.name.localeCompare(b.name)),
  }));

  const availableDealerships: DealershipWithGroup[] =
    sortedGroupsAndDealers.flatMap((group) =>
      (group.dealers || [])
        .filter((dealer) =>
          isLytefloUser || isGroupRole
            ? true
            : profile.dealers?.some(
                (userDealer) =>
                  userDealer.configurationId === dealer.configurationId,
              ),
        )
        .map((dealer) => ({
          ...dealer,
          groupName: group.name,
        })),
    );

  return {
    availableDealerships,
    dealerGroups: sortedGroupsAndDealers,
    context,
    setContext,
    isLoading,
    isGroupRole,
    isLytefloUser,
  };
};

export { DEALER_CONTEXT_TYPE };
